import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { calculateEEATScore, scrapeArticle } from "./thunks";
import { EEATState } from "./types";

const initialState: EEATState = {
    loading: {
        scrape: false,
        analyze: false,
    },
    error: {
        scrape: null,
        analyze: null,
    },
    url: "",
    scrapedText: null,
    result: null,
};

const eeatSlice = createSlice({
    name: "eeat",
    initialState,
    reducers: {
        setUrl: (state, action: PayloadAction<string>) => {
            state.url = action.payload;
        },
        resetEEAT: () => initialState,
        resetErrors: (state) => {
            state.error.scrape = null;
            state.error.analyze = null;
        },
    },
    extraReducers: (builder) => {
        // Handle scrapeArticle thunk
        builder
            .addCase(scrapeArticle.pending, (state) => {
                state.loading.scrape = true;
                state.error.scrape = null;
            })
            .addCase(scrapeArticle.fulfilled, (state, action) => {
                state.loading.scrape = false;
                if (action.payload.success) {
                    // Handle both new array format and legacy format
                    if (Array.isArray(action.payload.data)) {
                        state.scrapedText = action.payload.data[0] || "";
                    } else if (action.payload.article_text) {
                        state.scrapedText = action.payload.article_text;
                    } else {
                        state.scrapedText = "";
                    }
                } else {
                    state.error.scrape = action.payload.error || "Failed to scrape article";
                }
            })
            .addCase(scrapeArticle.rejected, (state, action) => {
                state.loading.scrape = false;
                // rejectWithValue carries the message; action.error.message is just "Rejected".
                state.error.scrape = (action.payload as any)?.error || "Failed to scrape article";
            });

        // Handle calculateEEATScore thunk
        builder
            .addCase(calculateEEATScore.pending, (state) => {
                state.loading.analyze = true;
                state.error.analyze = null;
            })
            .addCase(calculateEEATScore.fulfilled, (state, action) => {
                state.loading.analyze = false;
                state.result = action.payload;
            })
            .addCase(calculateEEATScore.rejected, (state, action) => {
                state.loading.analyze = false;
                state.error.analyze = (action.payload as any)?.error || "Failed to calculate EEAT score";
            });
    },
});

export const { setUrl, resetEEAT, resetErrors } = eeatSlice.actions;

export default eeatSlice.reducer;

export * from "./thunks";
export * from "./types";

// Selectors
export const selectEEATState = (state: { eeat: EEATState }) => state.eeat;
export const selectEEATResult = (state: { eeat: EEATState }) => state.eeat.result;
export const selectEEATLoading = (state: { eeat: EEATState }) => state.eeat.loading;
export const selectEEATError = (state: { eeat: EEATState }) => state.eeat.error;